// @givmo/app-charity — the intake doors (CBO-IN4, surface 3).
//
// ══ WHAT THIS SCREEN IS ════════════════════════════════════════════════════════════════════════
//
// The open-ended surface AFTER setup: the organization's books exist, and this is where what came
// before them gets added, a piece at a time. It has no end state, and that is the design rather than
// an unfinished part of it — no real charity arrives with complete historical books, so a page that
// worked towards "done" would be promising something nobody reaches.
//
// ⚠ NOT A WIZARD AND NOT A PROGRESS BAR. There is no step counter, no percentage and no "almost
// there". What is rendered is a set of questions about the organization's own books and which of
// them are still waiting — a fact — never how far along anyone is.
//
// ⚠ AND IT NEVER SAYS THE BOOKS ARE READY. Readiness derives from the books' own state, which this
// console cannot read at all; what it can see is the questions IT asked. The open-items copy says
// exactly that and claims nothing beyond it.
//
// ══ THE FIVE GATES, IN ORDER ═══════════════════════════════════════════════════════════════════
//
//   1. ORG — fail closed on `isReady`, as every screen in this feature does. No read fires against
//      an empty charity id.
//   2. MODULE — a 404 is "not available here", never an error, and never "the module is off": the
//      gate and a record outside the caller's scope answer byte-identically.
//   3. BOOKS — the doors render inputs only for a `provisioned` record, which is the backend's own
//      rule. Reaching here earlier is an ordinary thing to do (the address is guessable and setup is
//      where a person just was), so it reads as a signpost rather than a refusal.
//   4. ROLE — every write is Owner-only (`charity_enrollment:write`); the reads need only
//      `charity:read`. A Member sees everything and is offered no control the backend would refuse.
//   5. THE RECORDED CHOICE — which doors exist at all. Opening balances and full history are
//      different shapes, and the choice decides which rails a run may drive.
//
// ══ WHAT THIS SCREEN DELIBERATELY DOES NOT DO ═════════════════════════════════════════════════
//
// **No bulk confirmation, in any form.** Every mapping row and every opening figure is answered on
// its own. There is no select-all, no "confirm the rest", no multi-row control, and no page-level
// "these all look right" — that is the whole-document step the suite's document rules say exists
// nowhere in onboarding, and it would be that step whatever it was named. The backend has no
// list-shaped resolution route to call even if this screen wanted one, and `no-bulk-confirm` in the
// page test is what keeps that true through the next edit.
//
// **No client-side gate on whether a run may proceed.** This page can see at most a page of rows;
// the server sees the whole set and is the only thing that decides. So the run control is offered
// and the SERVER'S refusal is rendered — verbatim, because it names the accounts still waiting. A
// control disabled on a partial view would sometimes read "ready" over a set the server refuses,
// which is a wrong answer wearing a disabled attribute. The refusals that a person can fix in place
// are also the ones the backend refuses BEFORE recording an attempt, so pressing early is fully
// recoverable — the design and this posture were built for each other.
//
// **No "connect your bank" affordance, in any wording.** Three of the four rails read an export
// taken out of the system an organization is leaving, and this build holds no path for one. They are
// named, described, and stated to be unbuilt; nothing about them is pressable. An affordance for a
// rail that does not exist is a promise made by a button.
//
// The root keeps `data-testid="page-enrollment-backfill"`.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorMessage,
  Icon,
  PageHeader,
  Skeleton,
  TextField,
  testIdProp,
} from '@givmo/design-system';
import { BookOpen, Building2, ShieldAlert } from 'lucide-react';
import { isOwnerRole } from '../admins/admins-roles';
import { useActiveOrg } from '../../org/active-org-context';
import {
  BACKFILL_CHOICES,
  displayEnrollmentDate,
  isModuleUnavailable,
  isSetUp,
  readEnrollmentDetail,
  serverMessage,
  type BackfillChoice,
} from './enrollment-decode';
import { getEnrollment } from './enrollment-transport';
import {
  displayCents,
  extractMappingRows,
  extractOnboardingRecords,
  extractOpeningBalanceFields,
  isAwaitingAnAnswer,
  isExecutableRail,
  parseCents,
  readImportRun,
  type ImportRail,
  type ImportRun,
  type MappingRow,
  type OnboardingRecord,
  type OpeningBalanceField,
} from './enrollment-backfill-decode';
import {
  listMappingRows,
  listOnboardingRecords,
  listOpeningBalanceFields,
  recordBackfillChoice,
  resolveMappingRow,
  resolveOpeningBalanceField,
  seedOpeningBalanceFields,
  startImportRun,
} from './enrollment-backfill-transport';
import {
  BACKFILL_COPY,
  mapKindLabel,
  onboardingRecordKindLabel,
  railVocab,
  resolutionVocab,
  runStateVocab,
} from './enrollment-backfill-vocab';
import { ENROLLMENT_ROUTES } from './enrollment-routes';
import { enrollmentRecordKey } from './enrollment-record-page';

const PREFIX = 'enrollment-backfill';

/**
 * The rails each recorded choice admits — the backend's own `RAILS_BY_BACKFILL_CHOICE`.
 *
 * Mirrored so the page can NAME the rails a choice opens before anyone presses anything. It is not a
 * gate: a rail the backend does not admit is refused there, in a sentence this page renders.
 */
const RAILS_BY_CHOICE: Record<BackfillChoice, readonly ImportRail[]> = {
  opening_balances: ['opening_balances'],
  full_history: ['qbo_history', 'card_activity', 'bank_activity'],
};

/** The mapping queue's cache key. Hand-written; no generated family predicate matches it. */
export function mappingRowsKey(charityId: string | null, enrollmentId: string): readonly unknown[] {
  return ['enrollment', 'mapping-rows', charityId, enrollmentId];
}

export function openingBalancesKey(
  charityId: string | null,
  enrollmentId: string,
): readonly unknown[] {
  return ['enrollment', 'opening-balances', charityId, enrollmentId];
}

export function onboardingRecordsKey(
  charityId: string | null,
  enrollmentId: string,
): readonly unknown[] {
  return ['enrollment', 'onboarding-records', charityId, enrollmentId];
}

export interface EnrollmentBackfillPageProps {
  enrollmentId: string;
}

export function EnrollmentBackfillPage({ enrollmentId }: EnrollmentBackfillPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeMembership, activeCharityId, isReady, isPending, isError, refetch } =
    useActiveOrg();

  const isOwner = activeMembership !== null && isOwnerRole(activeMembership.role);

  const recordQuery = useQuery({
    // ⚠ THE SAME CACHE KEY THE RECORD PAGE USES, deliberately. Recording the choice answers with the
    // whole record, so seeding this key updates the record page too — and, more importantly, two
    // keys for one record is how a person sees the choice recorded here and not recorded there.
    queryKey: enrollmentRecordKey(activeCharityId, enrollmentId),
    queryFn: () => getEnrollment(activeCharityId ?? '', enrollmentId),
    select: readEnrollmentDetail,
    enabled: isReady && activeCharityId !== null,
  });

  const record = recordQuery.data ?? null;
  const booksExist = isSetUp(record);
  const choice = record?.backfillChoice ?? null;

  const mappingQuery = useQuery({
    queryKey: mappingRowsKey(activeCharityId, enrollmentId),
    queryFn: () => listMappingRows(activeCharityId ?? '', enrollmentId),
    select: extractMappingRows,
    // Rows can exist under EITHER choice, and the server's run gate checks them either way — so this
    // read is not gated on the choice. It is gated on the books, because nothing below a provisioned
    // record has rows at all.
    enabled: isReady && activeCharityId !== null && booksExist,
  });

  const openingQuery = useQuery({
    queryKey: openingBalancesKey(activeCharityId, enrollmentId),
    queryFn: () => listOpeningBalanceFields(activeCharityId ?? '', enrollmentId),
    select: extractOpeningBalanceFields,
    enabled: isReady && activeCharityId !== null && booksExist && choice === 'opening_balances',
  });

  const recordsQuery = useQuery({
    queryKey: onboardingRecordsKey(activeCharityId, enrollmentId),
    queryFn: () => listOnboardingRecords(activeCharityId ?? '', enrollmentId),
    select: extractOnboardingRecords,
    enabled: isReady && activeCharityId !== null && booksExist,
  });

  function refreshQueue() {
    void queryClient.invalidateQueries({ queryKey: mappingRowsKey(activeCharityId, enrollmentId) });
    void queryClient.invalidateQueries({
      queryKey: openingBalancesKey(activeCharityId, enrollmentId),
    });
  }

  function refreshAfterRun() {
    refreshQueue();
    void queryClient.invalidateQueries({
      queryKey: onboardingRecordsKey(activeCharityId, enrollmentId),
    });
    // The run writes onto the record's event log, so the record itself is stale too.
    void queryClient.invalidateQueries({
      queryKey: enrollmentRecordKey(activeCharityId, enrollmentId),
    });
  }

  const choiceMutation = useMutation({
    mutationFn: (next: BackfillChoice) =>
      recordBackfillChoice(activeCharityId ?? '', enrollmentId, next),
    onSuccess: (body) => {
      // The response IS the record, so the cache is seeded rather than refetched.
      queryClient.setQueryData(enrollmentRecordKey(activeCharityId, enrollmentId), body);
    },
  });

  // ── Org gate — fail closed ───────────────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError || !isReady) {
    return (
      <PageShell>
        <Retry
          testId={`${PREFIX}-org-error`}
          title="Couldn’t load your organization"
          description="We couldn’t load your organization. Please try again."
          onRetry={refetch}
        />
      </PageShell>
    );
  }

  // ── Module gate ──────────────────────────────────────────────────────────────────────────────────
  if (recordQuery.isError && isModuleUnavailable(recordQuery.error)) {
    return (
      <PageShell>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-unavailable`}
            icon={<Icon icon={Building2} size={20} aria-hidden />}
            title="This record isn’t available here"
            description="It may belong to a different organization, or this part of the console may not be switched on in this environment. Nothing your organization has recorded is affected."
          />
        </div>
      </PageShell>
    );
  }

  if (recordQuery.isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-record-loading`}>
          <Skeleton className="h-16 w-1/2" />
        </Card>
      </PageShell>
    );
  }

  if (recordQuery.isError) {
    return (
      <PageShell>
        <Retry
          testId={`${PREFIX}-error`}
          title="Couldn’t load this record"
          description={serverMessage(
            recordQuery.error,
            'We couldn’t load this record. Please try again.',
          )}
          onRetry={() => void recordQuery.refetch()}
        />
      </PageShell>
    );
  }

  // ── Books gate — the doors render inputs only for a provisioned record ────────────────────────────
  if (record === null || !booksExist) {
    return (
      <PageShell>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-not-yet`}
            icon={<Icon icon={BookOpen} size={20} aria-hidden />}
            title={BACKFILL_COPY.notYetTitle}
            description={BACKFILL_COPY.notYetBody}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void navigate({ to: ENROLLMENT_ROUTES.record(enrollmentId) })}
                testId={`${PREFIX}-not-yet-back`}
              >
                {BACKFILL_COPY.notYetAction}
              </Button>
            }
          />
        </div>
      </PageShell>
    );
  }

  const mappingRows = mappingQuery.data ?? [];
  const openingFields = openingQuery.data ?? [];
  const waitingRows = mappingRows.filter((row) => isAwaitingAnAnswer(row.resolution));
  const waitingFields = openingFields.filter((field) => isAwaitingAnAnswer(field.resolution));

  return (
    <PageShell>
      {choice === null ? (
        <ChoiceStep
          isOwner={isOwner}
          isRecording={choiceMutation.isPending}
          error={choiceMutation.isError ? choiceMutation.error : null}
          onChoose={(next) => choiceMutation.mutate(next)}
        />
      ) : (
        <RecordedChoice choice={choice} />
      )}

      {/* The queue renders whenever rows exist, under either choice — the server's run gate reads
          them either way, so hiding them under one choice would hide something that blocks. */}
      {mappingRows.length > 0 && (
        <MappingReview
          rows={mappingRows}
          isOwner={isOwner}
          charityId={activeCharityId ?? ''}
          enrollmentId={enrollmentId}
          onResolved={refreshQueue}
        />
      )}

      {choice === 'opening_balances' && (
        <OpeningBalances
          fields={openingFields}
          isOwner={isOwner}
          isLoading={openingQuery.isPending}
          charityId={activeCharityId ?? ''}
          enrollmentId={enrollmentId}
          onChanged={refreshQueue}
        />
      )}

      {(waitingRows.length > 0 || waitingFields.length > 0) && (
        <OpenItems rows={waitingRows} fields={waitingFields} />
      )}

      {choice !== null && (
        <RunPanel
          choice={choice}
          isOwner={isOwner}
          charityId={activeCharityId ?? ''}
          enrollmentId={enrollmentId}
          onRan={refreshAfterRun}
        />
      )}

      <OnboardingRecords
        records={recordsQuery.data ?? []}
        isLoading={recordsQuery.isPending}
        isError={recordsQuery.isError}
      />
    </PageShell>
  );
}

// ── The choice ─────────────────────────────────────────────────────────────────────────────────

/**
 * The two shapes, and the once-ness stated BEFORE anybody picks.
 *
 * ⚠ NEITHER OPTION IS PRE-SELECTED, RECOMMENDED, OR STYLED AS THE EXPECTED ANSWER — the review
 * queue's rail, applied here for the same reason. One of these is recorded permanently and the other
 * is then a correction somebody makes by hand, so the click has to cost something.
 *
 * ⚠ AND THE HISTORY OPTION IS OFFERED HONESTLY RATHER THAN HIDDEN. It is a real shape an
 * organization may genuinely need, and its rails are not built — so it is named, described, and says
 * so, with nothing pressable attached to the rails themselves. Hiding it would leave somebody
 * choosing opening balances without knowing the other thing exists; a live "connect your bank"
 * button would be a promise made by a control.
 */
function ChoiceStep({
  isOwner,
  isRecording,
  error,
  onChoose,
}: {
  isOwner: boolean;
  isRecording: boolean;
  error: unknown;
  onChoose: (choice: BackfillChoice) => void;
}) {
  return (
    <Card testId={`${PREFIX}-choice`}>
      <CardBody>
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s18 font-w600 text-neutral-900">
              {BACKFILL_COPY.choiceTitle}
            </h2>
            <p className="text-s14 text-neutral-700">{BACKFILL_COPY.choiceBody}</p>
            <p className="text-s12 text-neutral-600">{BACKFILL_COPY.choiceOnceHint}</p>
          </div>

          {BACKFILL_CHOICES.map((option) => {
            const rails = RAILS_BY_CHOICE[option];
            const executable = rails.some((rail) => isExecutableRail(rail));
            return (
              <div
                key={option}
                className="flex flex-col gap-10 rounded-lg border border-border-subtle p-16"
                {...testIdProp(`${PREFIX}-choice-${option}`)}
              >
                {rails.map((rail) => {
                  const vocab = railVocab(rail);
                  return (
                    <div key={rail} className="flex flex-col gap-4">
                      <span className="font-sans text-s14 font-w600 text-neutral-900">
                        {vocab.label}
                      </span>
                      <p className="text-s13 text-neutral-700">{vocab.description}</p>
                    </div>
                  );
                })}
                {/* The unbuilt shape says so once, for the whole group, and offers nothing. */}
                {!executable && (
                  <p
                    className="rounded-lg bg-neutral-50 px-12 py-10 text-s13 text-neutral-700"
                    {...testIdProp(`${PREFIX}-choice-${option}-unavailable`)}
                  >
                    {BACKFILL_COPY.historyUnavailableBody}
                  </p>
                )}
                {isOwner && (
                  <div>
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      onClick={() => onChoose(option)}
                      disabled={isRecording}
                      testId={`${PREFIX}-choice-${option}-action`}
                    >
                      {BACKFILL_COPY.choiceAction}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}

          {!isOwner && (
            <p className="text-s12 text-neutral-600" {...testIdProp(`${PREFIX}-choice-owner-only`)}>
              An Owner of your organization can record this.
            </p>
          )}
          {error !== null && (
            <ErrorMessage testId={`${PREFIX}-choice-error`}>
              {serverMessage(error, 'We couldn’t record that. Please try again.')}
            </ErrorMessage>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** What was recorded. No control to change it — the backend treats a different answer as a 409. */
function RecordedChoice({ choice }: { choice: BackfillChoice }) {
  const rails = RAILS_BY_CHOICE[choice];
  return (
    <Card testId={`${PREFIX}-choice-recorded`}>
      <CardBody>
        <div className="flex flex-col gap-8">
          <span className="text-s12 text-neutral-600">{BACKFILL_COPY.choiceRecordedLabel}</span>
          <span className="font-sans text-s16 font-w600 text-neutral-900">
            {railVocab(rails[0] ?? null).label}
          </span>
          <p className="text-s13 text-neutral-700">{railVocab(rails[0] ?? null).description}</p>
        </div>
      </CardBody>
    </Card>
  );
}

// ── The three-way answer ───────────────────────────────────────────────────────────────────────

/**
 * The confirm-or-correct control, for ONE value.
 *
 * ⚠ THREE RAILS, none of them stylistic:
 *
 *   1. **NOTHING IS PRE-SELECTED.** No answer arrives chosen, defaulted or highlighted as expected.
 *   2. **THE THREE READ WITH EQUAL WEIGHT** — same row, same size, same tone. Making "confirm"
 *      the filled primary would make agreeing the path of least resistance, which is how a decision
 *      becomes a formality.
 *   3. **CONFIRM IS ABSENT WHEN THERE IS NOTHING TO CONFIRM.** The backend refuses a confirmation
 *      with no proposal by name — confirming nothing would record that a person agreed with a value
 *      that was never offered — so the affordance is absent rather than present-and-refused.
 *
 * The correction input is whatever the caller passes: an account code here, an amount there.
 */
function ThreeWayAnswer({
  testId,
  canConfirm,
  correctLabel,
  correctPlaceholder,
  noteLabel,
  onConfirm,
  onCorrect,
  onDecline,
  isSaving,
  error,
  validate,
}: {
  testId: string;
  canConfirm: boolean;
  correctLabel: string;
  correctPlaceholder?: string;
  noteLabel: string;
  onConfirm: (note: string) => void;
  onCorrect: (value: string, note: string) => void;
  onDecline: (note: string) => void;
  isSaving: boolean;
  error: unknown;
  /** Returns an error sentence for a value this control must not submit, or `null` when it is fine. */
  validate?: (value: string) => string | null;
}) {
  const [step, setStep] = useState<'idle' | 'correcting'>('idle');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');

  const localError =
    step === 'correcting' && value.trim().length > 0 ? (validate?.(value) ?? null) : null;
  const canSubmitCorrection = value.trim().length > 0 && localError === null;

  if (step === 'correcting') {
    return (
      <div className="flex flex-col gap-10" {...testIdProp(`${testId}-correcting`)}>
        <TextField
          label={correctLabel}
          placeholder={correctPlaceholder}
          autoComplete="off"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          testId={`${testId}-correct-input`}
        />
        {localError !== null && (
          <ErrorMessage testId={`${testId}-correct-invalid`}>{localError}</ErrorMessage>
        )}
        <TextField
          label={noteLabel}
          autoComplete="off"
          maxLength={2000}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          testId={`${testId}-correct-note`}
        />
        <div className="flex flex-wrap gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => onCorrect(value, note)}
            disabled={!canSubmitCorrection || isSaving}
            testId={`${testId}-correct-save`}
          >
            {BACKFILL_COPY.correctAction}
          </Button>
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            onClick={() => {
              setStep('idle');
              setValue('');
            }}
            testId={`${testId}-correct-cancel`}
          >
            {BACKFILL_COPY.cancelAction}
          </Button>
        </div>
        {error !== null && (
          <ErrorMessage testId={`${testId}-error`}>
            {serverMessage(error, 'We couldn’t record that. Please try again.')}
          </ErrorMessage>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      {/* ⚠ SYMMETRY. Same size, same weight, same row, none pre-selected, none styled as the
          expected answer. Do not promote one of these to a filled primary. */}
      <div className="flex flex-wrap gap-8" {...testIdProp(`${testId}-answers`)}>
        {canConfirm && (
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => onConfirm(note)}
            disabled={isSaving}
            testId={`${testId}-confirm`}
          >
            {BACKFILL_COPY.confirmAction}
          </Button>
        )}
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => setStep('correcting')}
          disabled={isSaving}
          testId={`${testId}-correct`}
        >
          {BACKFILL_COPY.correctAction}
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => onDecline(note)}
          disabled={isSaving}
          testId={`${testId}-decline`}
        >
          {BACKFILL_COPY.declineAction}
        </Button>
      </div>
      <p className="text-s12 text-neutral-600">{BACKFILL_COPY.declineHint}</p>
      {error !== null && (
        <ErrorMessage testId={`${testId}-error`}>
          {serverMessage(error, 'We couldn’t record that. Please try again.')}
        </ErrorMessage>
      )}
    </div>
  );
}

// ── Mapping review ─────────────────────────────────────────────────────────────────────────────

/**
 * Every source account and merchant, and where each one posts.
 *
 * ⚠ THIS IS THE CONTROL, NOT A FORMALITY. The tie a run computes proves that everything the source
 * held came across and that each source account's total matches — and it validates NO account
 * assignment at all. A mis-mapped account ties perfectly and lands in the wrong place. Which is why
 * every row is answered on its own and there is no way to answer them together.
 */
function MappingReview({
  rows,
  isOwner,
  charityId,
  enrollmentId,
  onResolved,
}: {
  rows: readonly MappingRow[];
  isOwner: boolean;
  charityId: string;
  enrollmentId: string;
  onResolved: () => void;
}) {
  return (
    <Card testId={`${PREFIX}-mapping`}>
      <CardBody>
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {BACKFILL_COPY.mappingTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{BACKFILL_COPY.mappingBody}</p>
          </div>
          <ul className="flex flex-col gap-12">
            {rows.map((row) => (
              <MappingRowCard
                key={row.id}
                row={row}
                isOwner={isOwner}
                charityId={charityId}
                enrollmentId={enrollmentId}
                onResolved={onResolved}
              />
            ))}
          </ul>
        </div>
      </CardBody>
    </Card>
  );
}

function MappingRowCard({
  row,
  isOwner,
  charityId,
  enrollmentId,
  onResolved,
}: {
  row: MappingRow;
  isOwner: boolean;
  charityId: string;
  enrollmentId: string;
  onResolved: () => void;
}) {
  const resolve = useMutation({
    mutationFn: (body: { resolution: string; account_code?: string; note?: string }) =>
      resolveMappingRow(charityId, enrollmentId, row.id, body),
    onSuccess: onResolved,
  });

  const vocab = resolutionVocab(row.resolution);
  const answered = !isAwaitingAnAnswer(row.resolution);

  return (
    <li
      className="flex flex-col gap-10 rounded-lg border border-border-subtle p-14"
      {...testIdProp(`${PREFIX}-mapping-row-${row.id}`)}
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={vocab.tone} testId={`${PREFIX}-mapping-row-${row.id}-state`}>
          {vocab.label}
        </Badge>
        <span className="text-s12 text-neutral-600">{mapKindLabel(row.mapKind)}</span>
      </div>
      {row.subjectKey !== null && (
        <span className="font-sans text-s14 font-w600 text-neutral-900">
          {row.subjectLabel === null ? row.subjectKey : `${row.subjectKey} — ${row.subjectLabel}`}
        </span>
      )}
      {/* Proposed and resolved are rendered separately, never merged: a reader of this row alone can
          see what was offered and what a person actually stood behind. */}
      {row.proposedAccountCode === null ? (
        <span className="text-s13 text-neutral-600">{BACKFILL_COPY.mappingNoProposal}</span>
      ) : (
        <span className="text-s13 text-neutral-700">
          {BACKFILL_COPY.mappingProposedLabel} {row.proposedAccountCode}
        </span>
      )}
      {row.resolvedAccountCode !== null && (
        <span
          className="text-s13 text-neutral-900"
          {...testIdProp(`${PREFIX}-mapping-row-${row.id}-resolved`)}
        >
          {BACKFILL_COPY.mappingProposedLabel} {row.resolvedAccountCode}
        </span>
      )}
      {/* The organization's own note, rendered verbatim — never paraphrased. */}
      {row.resolutionNote !== null && (
        <p className="rounded-lg bg-neutral-50 px-12 py-8 text-s12 text-neutral-700">
          {row.resolutionNote}
        </p>
      )}
      {isOwner && (
        <ThreeWayAnswer
          testId={`${PREFIX}-mapping-row-${row.id}`}
          canConfirm={row.proposedAccountCode !== null}
          correctLabel={BACKFILL_COPY.mappingCorrectLabel}
          noteLabel={BACKFILL_COPY.mappingNoteLabel}
          isSaving={resolve.isPending}
          error={resolve.isError ? resolve.error : null}
          onConfirm={(note) =>
            resolve.mutate({
              resolution: 'confirmed',
              ...(note.trim() ? { note: note.trim() } : {}),
            })
          }
          onCorrect={(value, note) =>
            resolve.mutate({
              resolution: 'corrected',
              account_code: value.trim(),
              ...(note.trim() ? { note: note.trim() } : {}),
            })
          }
          onDecline={(note) =>
            resolve.mutate({
              resolution: 'declined',
              ...(note.trim() ? { note: note.trim() } : {}),
            })
          }
        />
      )}
      {/* An answered row keeps its controls (an answer is re-answerable) — this line only records
          WHEN, so a reader can tell a fresh answer from an old one. */}
      {answered && row.resolvedAt !== null && (
        <span className="text-s12 text-neutral-600">
          Answered {displayEnrollmentDate(row.resolvedAt)}
        </span>
      )}
    </li>
  );
}

// ── Opening balances ───────────────────────────────────────────────────────────────────────────

/**
 * Each account's opening figure, answered on its own.
 *
 * ⚠ SEEDING PROPOSES; IT RECORDS NOTHING. Adding an account puts it in front of the organization
 * with a figure attached, and the row stays unanswered until somebody answers it — so the add form
 * is not a back door around the per-field rule. It adds ONE account at a time, which is also what
 * "the charity keys in per-account figures" actually looks like.
 */
function OpeningBalances({
  fields,
  isOwner,
  isLoading,
  charityId,
  enrollmentId,
  onChanged,
}: {
  fields: readonly OpeningBalanceField[];
  isOwner: boolean;
  isLoading: boolean;
  charityId: string;
  enrollmentId: string;
  onChanged: () => void;
}) {
  return (
    <Card testId={`${PREFIX}-opening`}>
      <CardBody>
        <div className="flex flex-col gap-16">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {BACKFILL_COPY.openingTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{BACKFILL_COPY.openingBody}</p>
          </div>

          {isOwner && (
            <SeedAccountForm
              charityId={charityId}
              enrollmentId={enrollmentId}
              onSeeded={onChanged}
            />
          )}

          {isLoading ? (
            <Skeleton className="h-16 w-1/2" />
          ) : fields.length === 0 ? (
            <p className="text-s13 text-neutral-600" {...testIdProp(`${PREFIX}-opening-empty`)}>
              {BACKFILL_COPY.openingEmpty}
            </p>
          ) : (
            <ul className="flex flex-col gap-12">
              {fields.map((field) => (
                <OpeningBalanceFieldCard
                  key={field.id}
                  field={field}
                  isOwner={isOwner}
                  charityId={charityId}
                  enrollmentId={enrollmentId}
                  onResolved={onChanged}
                />
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/** Add ONE account, with the figure proposed for it. */
function SeedAccountForm({
  charityId,
  enrollmentId,
  onSeeded,
}: {
  charityId: string;
  enrollmentId: string;
  onSeeded: () => void;
}) {
  const [accountCode, setAccountCode] = useState('');
  const [amount, setAmount] = useState('');

  const seed = useMutation({
    mutationFn: (cents: number | null) =>
      seedOpeningBalanceFields(charityId, enrollmentId, [
        {
          account_code: accountCode.trim(),
          // ⚠ `null` and `0` are different answers all the way down: an account added with no figure
          // is one nobody has proposed anything for, and it must not arrive as a zero.
          proposed_amount_cents: cents,
        },
      ]),
    onSuccess: () => {
      setAccountCode('');
      setAmount('');
      onSeeded();
    },
  });

  const typed = amount.trim();
  const cents = typed.length === 0 ? null : parseCents(typed);
  const amountInvalid = typed.length > 0 && cents === null;
  const canSubmit = accountCode.trim().length > 0 && !amountInvalid && !seed.isPending;

  return (
    <div className="flex flex-col gap-10 rounded-lg border border-border-subtle p-14">
      <div className="flex flex-col gap-4">
        <h3 className="font-sans text-s14 font-w600 text-neutral-900">
          {BACKFILL_COPY.openingAddTitle}
        </h3>
        <p className="text-s12 text-neutral-600">{BACKFILL_COPY.openingAddBody}</p>
      </div>
      <TextField
        label={BACKFILL_COPY.openingAccountLabel}
        autoComplete="off"
        maxLength={32}
        value={accountCode}
        onChange={(event) => setAccountCode(event.target.value)}
        testId={`${PREFIX}-opening-add-account`}
      />
      <TextField
        label={BACKFILL_COPY.openingProposedLabel}
        autoComplete="off"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        testId={`${PREFIX}-opening-add-amount`}
      />
      <p className="text-s12 text-neutral-600">{BACKFILL_COPY.openingSignHint}</p>
      {amountInvalid && (
        <ErrorMessage testId={`${PREFIX}-opening-add-invalid`}>
          Enter an amount in dollars and cents, or leave it empty.
        </ErrorMessage>
      )}
      <div>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => seed.mutate(cents)}
          disabled={!canSubmit}
          testId={`${PREFIX}-opening-add-action`}
        >
          {BACKFILL_COPY.openingAddAction}
        </Button>
      </div>
      {seed.isError && (
        <ErrorMessage testId={`${PREFIX}-opening-add-error`}>
          {serverMessage(seed.error, 'We couldn’t add that account. Please try again.')}
        </ErrorMessage>
      )}
    </div>
  );
}

function OpeningBalanceFieldCard({
  field,
  isOwner,
  charityId,
  enrollmentId,
  onResolved,
}: {
  field: OpeningBalanceField;
  isOwner: boolean;
  charityId: string;
  enrollmentId: string;
  onResolved: () => void;
}) {
  const resolve = useMutation({
    mutationFn: (body: { resolution: string; amount_cents?: number; source_note?: string }) =>
      resolveOpeningBalanceField(charityId, enrollmentId, field.id, body),
    onSuccess: onResolved,
  });

  const vocab = resolutionVocab(field.resolution);

  return (
    <li
      className="flex flex-col gap-10 rounded-lg border border-border-subtle p-14"
      {...testIdProp(`${PREFIX}-opening-field-${field.id}`)}
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={vocab.tone} testId={`${PREFIX}-opening-field-${field.id}-state`}>
          {vocab.label}
        </Badge>
        {field.accountCode !== null && (
          <span className="font-sans text-s14 font-w600 text-neutral-900">{field.accountCode}</span>
        )}
      </div>
      {/* ⚠ ABSENT IS NOT ZERO. An account with no proposal says so in words rather than rendering
          `$0.00`, which would be a figure somebody could confirm without noticing. */}
      {field.proposedAmountCents === null ? (
        <span className="text-s13 text-neutral-600">{BACKFILL_COPY.openingNoFigure}</span>
      ) : (
        <span className="text-s13 text-neutral-700">
          {BACKFILL_COPY.openingProposedFor} {displayCents(field.proposedAmountCents)}
        </span>
      )}
      {field.recordedAmountCents !== null && (
        <span
          className="text-s13 text-neutral-900"
          {...testIdProp(`${PREFIX}-opening-field-${field.id}-recorded`)}
        >
          {BACKFILL_COPY.openingRecordedFor} {displayCents(field.recordedAmountCents)}
        </span>
      )}
      {field.sourceNote !== null && (
        <p className="rounded-lg bg-neutral-50 px-12 py-8 text-s12 text-neutral-700">
          {field.sourceNote}
        </p>
      )}
      {isOwner && (
        <ThreeWayAnswer
          testId={`${PREFIX}-opening-field-${field.id}`}
          canConfirm={field.proposedAmountCents !== null}
          correctLabel={BACKFILL_COPY.openingCorrectLabel}
          noteLabel={BACKFILL_COPY.openingSourceLabel}
          isSaving={resolve.isPending}
          error={resolve.isError ? resolve.error : null}
          // ⚠ REFUSED, NOT ROUNDED. What is recorded here becomes a line of an entry nobody can edit
          // afterwards, so a third decimal place comes back for a fix rather than turning into a
          // figure the person did not type.
          validate={(value) =>
            parseCents(value) === null
              ? 'Enter an amount in dollars and cents. A credit balance takes a minus sign.'
              : null
          }
          onConfirm={(note) =>
            resolve.mutate({
              resolution: 'confirmed',
              // ⚠ NO FIGURE ON A CONFIRMATION. The backend reads a different figure as a CORRECTION
              // and refuses it by name; sending the proposal back "to be explicit" is how that
              // refusal happens.
              ...(note.trim() ? { source_note: note.trim() } : {}),
            })
          }
          onCorrect={(value, note) => {
            const cents = parseCents(value);
            if (cents === null) return;
            resolve.mutate({
              resolution: 'corrected',
              amount_cents: cents,
              ...(note.trim() ? { source_note: note.trim() } : {}),
            });
          }}
          onDecline={(note) =>
            resolve.mutate({
              resolution: 'declined',
              ...(note.trim() ? { source_note: note.trim() } : {}),
            })
          }
        />
      )}
      {field.resolvedAt !== null && (
        <span className="text-s12 text-neutral-600">
          Answered {displayEnrollmentDate(field.resolvedAt)}
        </span>
      )}
    </li>
  );
}

// ── Open items ─────────────────────────────────────────────────────────────────────────────────

/**
 * What this page has asked and not had an answer to.
 *
 * ⚠ SURFACED, NEVER SUPPRESSED — and scoped honestly. This console can see the questions IT asked;
 * the books have open items of their own (a suspense balance blocking a close, for one) that it
 * cannot read at all. So the copy says exactly that, and nothing here is a statement that the books
 * are otherwise clear.
 */
function OpenItems({
  rows,
  fields,
}: {
  rows: readonly MappingRow[];
  fields: readonly OpeningBalanceField[];
}) {
  const items = useMemo(
    () => [
      ...rows.map((row) => row.subjectKey ?? row.id),
      ...fields.map((field) => field.accountCode ?? field.id),
    ],
    [rows, fields],
  );

  return (
    <Card testId={`${PREFIX}-open-items`}>
      <CardBody>
        <div className="flex flex-col gap-10">
          <h2 className="font-sans text-s14 font-w600 text-neutral-900">
            {BACKFILL_COPY.openItemsTitle}
          </h2>
          <p className="text-s13 text-neutral-700">{BACKFILL_COPY.openItemsBody}</p>
          <ul className="flex flex-wrap gap-8">
            {items.map((item) => (
              <li key={item} className="text-s12 text-neutral-700">
                <Badge tone="warning">{item}</Badge>
              </li>
            ))}
          </ul>
        </div>
      </CardBody>
    </Card>
  );
}

// ── The run ────────────────────────────────────────────────────────────────────────────────────

/**
 * Bring it in.
 *
 * ⚠ THE IDEMPOTENCY KEY IS A DECISION, NOT A VALUE THIS SCREEN REGENERATES ON EVERY CLICK. It is
 * built from the rail, the date, and a nonce that changes only on an explicit act:
 *
 *   • **Trying again reuses it**, which is what makes a retry safe after a response nobody saw. If
 *     the attempt actually landed, the replay answers with the run it already named instead of
 *     posting a second copy of the organization's opening position.
 *   • **Changing the date mints a new one automatically**, because a cut-in as at a different day is
 *     a different act — and reusing the key would answer with the run for the OLD date, which the
 *     screen would then render as though the new date had run.
 *   • **"Start a new attempt" mints one deliberately.** After a refusal that was recorded, the key is
 *     spent, and the person is the only one who knows whether they mean a second act.
 *
 * ⚠ A REFUSED RUN IS A RESULT, RENDERED IN PLACE. The backend both raises the refusal AND writes it
 * on the run, so it can arrive as an HTTP error or as a 200 body carrying `run_state: refused`. Both
 * are the same event and both render the same way — the recorded reason, verbatim, because it names
 * the accounts still waiting or what the figures net to.
 */
function RunPanel({
  choice,
  isOwner,
  charityId,
  enrollmentId,
  onRan,
}: {
  choice: BackfillChoice;
  isOwner: boolean;
  charityId: string;
  enrollmentId: string;
  onRan: () => void;
}) {
  const rails = RAILS_BY_CHOICE[choice];
  const executable = rails.filter((rail) => isExecutableRail(rail));

  const [effectiveDate, setEffectiveDate] = useState('');
  const [nonce, setNonce] = useState(() => newNonce());

  const run = useMutation({
    mutationFn: (rail: ImportRail) =>
      startImportRun(charityId, enrollmentId, {
        rail,
        idempotency_key: `${rail}:${effectiveDate}:${nonce}`,
        effective_date: effectiveDate,
        // ⚠ THE PERIOD IS DELIBERATELY ABSENT FROM THIS BODY. A cut-in states a position as at a
        // date and the backend DERIVES the period from it; supplying both invites a mismatch, which
        // it refuses rather than silently resolving — correctly, since that would settle two beliefs
        // about when this happened inside a journal nobody can edit afterwards.
      }),
    onSuccess: onRan,
  });

  const result = run.isSuccess ? readImportRun(run.data) : null;

  if (executable.length === 0) {
    return (
      <Card testId={`${PREFIX}-run-unavailable`}>
        <CardBody>
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {BACKFILL_COPY.historyUnavailableTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{BACKFILL_COPY.historyUnavailableBody}</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card testId={`${PREFIX}-run`}>
      <CardBody>
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {BACKFILL_COPY.runTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{BACKFILL_COPY.runBody}</p>
          </div>

          {isOwner && (
            <>
              <TextField
                type="date"
                label="The day your books start"
                value={effectiveDate}
                onChange={(event) => setEffectiveDate(event.target.value)}
                testId={`${PREFIX}-run-date`}
              />
              <div className="flex flex-wrap gap-8">
                {executable.map((rail) => (
                  <Button
                    key={rail}
                    appearance="saas"
                    tone="primary"
                    size="md"
                    onClick={() => run.mutate(rail)}
                    disabled={effectiveDate.length === 0 || run.isPending}
                    testId={`${PREFIX}-run-action`}
                  >
                    {BACKFILL_COPY.runAction}
                  </Button>
                ))}
                <Button
                  appearance="saas"
                  tone="ghost"
                  size="sm"
                  onClick={() => {
                    setNonce(newNonce());
                    run.reset();
                  }}
                  testId={`${PREFIX}-run-new-attempt`}
                >
                  {BACKFILL_COPY.runNewAttemptAction}
                </Button>
              </div>
              <p className="text-s12 text-neutral-600">{BACKFILL_COPY.runNewAttemptHint}</p>
            </>
          )}

          {/* The refusal as an HTTP error — the attempt raised rather than answering. */}
          {run.isError && (
            <ErrorMessage testId={`${PREFIX}-run-error`}>
              {serverMessage(run.error, 'We couldn’t bring this in. Please try again.')}
            </ErrorMessage>
          )}

          {/* The SAME refusal as a 200 body, when a spent key was replayed — and a bound run, which
              arrives through exactly this branch too. */}
          {result !== null && <RunResult run={result} />}
        </div>
      </CardBody>
    </Card>
  );
}

/** One run's outcome, rendered the same way whichever of the two shapes it arrived in. */
function RunResult({ run }: { run: ImportRun }) {
  const vocab = runStateVocab(run.runState);
  return (
    <div
      className="flex flex-col gap-8 rounded-lg border border-border-subtle p-14"
      {...testIdProp(`${PREFIX}-run-result`)}
    >
      <div className="flex flex-wrap items-center gap-8">
        <Badge tone={vocab.tone} testId={`${PREFIX}-run-result-state`}>
          {vocab.label}
        </Badge>
        {run.period !== null && <span className="text-s12 text-neutral-600">{run.period}</span>}
      </div>
      <p className="text-s13 text-neutral-700">{vocab.description}</p>
      {/* ⚠ THE RECORDED REASON, VERBATIM. It names the accounts still waiting, or what the figures
          net to — facts about the organization's own books that this console could not state better.
          `refusalType` is a machine name and is never rendered. */}
      {run.refusalDetail !== null && (
        <div className="flex flex-col gap-4">
          <span className="text-s12 text-neutral-600">{BACKFILL_COPY.runRefusalTitle}</span>
          <p
            className="rounded-lg bg-neutral-50 px-12 py-10 text-s13 text-neutral-700"
            {...testIdProp(`${PREFIX}-run-result-refusal`)}
          >
            {run.refusalDetail}
          </p>
        </div>
      )}
      {run.tieWaived && (
        <span className="text-s12 text-neutral-700" {...testIdProp(`${PREFIX}-run-result-waived`)}>
          {BACKFILL_COPY.runTieWaivedLabel}
          {run.tieWaiverReason === null ? '' : `: ${run.tieWaiverReason}`}
        </span>
      )}
    </div>
  );
}

/**
 * A fresh attempt nonce.
 *
 * `crypto.randomUUID` is available in every supported browser and in Node 19+. The generic
 * `IdempotencyKeyHolder` in `@givmo/payments` implements this same lifecycle, and it is deliberately
 * NOT reused here: that package exists for the Shift4 seam, and making the charity console a
 * payments consumer to borrow twelve lines would be the wrong dependency edge for the wrong reason.
 * The series identity lives in the key string itself (`rail:date:nonce`), which needs no holder.
 */
function newNonce(): string {
  return crypto.randomUUID();
}

// ── The onboarding records ─────────────────────────────────────────────────────────────────────

/**
 * The per-event stack.
 *
 * ⚠ AN EMPTY STACK RENDERS "COULDN'T BE READ", NOT "NOTHING YET". An organization whose books exist
 * always has at least the record of them being opened, so an empty array means this client could not
 * read the listing — and "nothing yet" would state as a fact about the organization something that
 * is only a fact about the read. The lifecycle page's event log carries the same rule for the same
 * reason.
 */
function OnboardingRecords({
  records,
  isLoading,
  isError,
}: {
  records: readonly OnboardingRecord[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <Card testId={`${PREFIX}-records`}>
      <CardBody>
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-8">
            <h2 className="font-sans text-s16 font-w600 text-neutral-900">
              {BACKFILL_COPY.recordsTitle}
            </h2>
            <p className="text-s13 text-neutral-700">{BACKFILL_COPY.recordsBody}</p>
          </div>
          {isLoading ? (
            <Skeleton className="h-16 w-1/2" />
          ) : isError || records.length === 0 ? (
            <p
              className="text-s13 text-neutral-600"
              {...testIdProp(`${PREFIX}-records-unreadable`)}
            >
              {BACKFILL_COPY.recordsUnreadable}
            </p>
          ) : (
            <ul className="flex flex-col gap-10">
              {records.map((record) => (
                <li
                  key={record.id}
                  className="flex flex-col gap-4 border-b border-border-subtle pb-10 last:border-0 last:pb-0"
                  {...testIdProp(`${PREFIX}-record-${record.id}`)}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-8">
                    <span className="text-s13 text-neutral-900">
                      {onboardingRecordKindLabel(record.recordKind)}
                    </span>
                    <span className="text-s12 text-neutral-600">
                      {displayEnrollmentDate(record.boundAt)}
                    </span>
                  </div>
                  {record.rulePackId !== null && (
                    <span className="text-s12 text-neutral-600">
                      {BACKFILL_COPY.recordsPackLabel}: {record.rulePackId}
                    </span>
                  )}
                  {/* The copy backlog, stated rather than hidden: a record whose second home has not
                      caught up is a real state, and it is not an error. */}
                  {record.storeCopiedAt === null && (
                    <span className="text-s12 text-neutral-600">
                      {BACKFILL_COPY.recordsPendingCopyLabel}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

// ── Shell ──────────────────────────────────────────────────────────────────────────────────────

function Retry({
  testId,
  title,
  description,
  onRetry,
}: {
  testId: string;
  title: string;
  description: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface">
      <EmptyState
        testId={testId}
        icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
        title={title}
        description={description}
        action={
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={onRetry}
            testId={`${testId}-retry`}
          >
            Try again
          </Button>
        }
      />
    </div>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[840px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-enrollment-backfill')}
    >
      <PageHeader
        title={BACKFILL_COPY.pageTitle}
        subtitle={BACKFILL_COPY.pageSubtitle}
        breadcrumbs={[{ label: 'Organization setup', href: ENROLLMENT_ROUTES.home }]}
      />
      {children}
    </div>
  );
}
